import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2 text-xs text-[color:var(--ink-2)]">{label}{children}</div>
);
const Sec = ({ title, children }) => (
  <div className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-3 space-y-2">
    <div className="text-[11px] font-semibold text-[color:var(--ink-2)] uppercase tracking-wide">{title}</div>
    {children}
  </div>
);

const FILTER_OPS = [["eq", "equals"], ["in", "in list"], ["gte", "≥"], ["lte", "≤"], ["contains", "contains"]];

function DatasetPicker({ cfg, set, datasets }) {
  return (
    <div>
      <Label className="text-[11px] text-[color:var(--ink-3)]">Dataset</Label>
      <Select value={cfg.dataset_id || ""} onValueChange={(v) => set({ dataset_id: v, columns: [], measures: [], dimension: null, group_by: null, group_by2: null, row_field: null, col_field: null, filters: [], calcs: [], cond: [] })}>
        <SelectTrigger data-testid="re-dataset" className="mt-1 h-8 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="Pick dataset" /></SelectTrigger>
        <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

const isNum = (t) => ["int", "float", "number", "numeric", "decimal"].some((k) => String(t || "").toLowerCase().includes(k));

function fieldsOf(datasets, id) { return (datasets.find((d) => d.id === id)?.schema) || []; }

const WIDTH_OPTS = [["full", "Full"], ["1/2", "½ Half"], ["1/3", "⅓ Third"], ["2/3", "⅔ Two-thirds"]];
function WidthRow({ cfg, set }) {
  return (
    <Row label="Width">
      <Select value={cfg.width || "full"} onValueChange={(v) => set({ width: v })}>
        <SelectTrigger data-testid="re-width" className="h-7 w-[110px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
        <SelectContent>{WIDTH_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </Row>
  );
}

function MeasureList({ cfg, set, datasets }) {
  const numFields = fieldsOf(datasets, cfg.dataset_id).filter((c) => isNum(c.dtype || c.type)).map((c) => c.name);
  const measures = cfg.measures || [];
  const patch = (i, p) => set({ measures: measures.map((m, j) => (j === i ? { ...m, ...p } : m)) });
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] text-[color:var(--ink-3)]">Measures</Label>
      {measures.map((m, i) => (
        <div key={i} className="flex items-center gap-1">
          <Select value={m.field || ""} onValueChange={(v) => patch(i, { field: v })}>
            <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)] flex-1"><SelectValue placeholder="field" /></SelectTrigger>
            <SelectContent>{numFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={m.agg || "sum"} onValueChange={(v) => patch(i, { agg: v })}>
            <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)] w-[84px]"><SelectValue /></SelectTrigger>
            <SelectContent>{["sum", "avg", "count", "count_distinct", "min", "max", "median"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={() => set({ measures: measures.filter((_, j) => j !== i) })} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <Button data-testid="re-add-measure" variant="outline" size="sm" onClick={() => set({ measures: [...measures, { field: numFields[0], agg: "sum" }] })} className="h-7 text-xs bg-[color:var(--surface-2)]"><Plus className="w-3 h-3 mr-1" />Measure</Button>
    </div>
  );
}

function ElementFilters({ cfg, set, datasets }) {
  const fields = fieldsOf(datasets, cfg.dataset_id).map((c) => c.name);
  const filters = cfg.filters || [];
  const patch = (i, p) => set({ filters: filters.map((f, j) => (j === i ? { ...f, ...p } : f)) });
  return (
    <div className="space-y-1.5 pt-1 border-t border-[color:var(--glass-border)]">
      <Label className="text-[11px] text-[color:var(--ink-3)]">Element filters</Label>
      {filters.map((f, i) => (
        <div key={i} className="flex items-center gap-1">
          <Select value={f.field || ""} onValueChange={(v) => patch(i, { field: v })}>
            <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)] flex-1"><SelectValue placeholder="field" /></SelectTrigger>
            <SelectContent>{fields.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.op || "eq"} onValueChange={(v) => patch(i, { op: v })}>
            <SelectTrigger className="h-7 w-[74px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>{FILTER_OPS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={f.value ?? ""} onChange={(e) => patch(i, { value: e.target.value })} placeholder="value" className="h-7 w-[76px] text-xs bg-[color:var(--surface-2)]" />
          <button onClick={() => set({ filters: filters.filter((_, j) => j !== i) })} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <Button data-testid="re-add-filter" variant="outline" size="sm" onClick={() => set({ filters: [...filters, { field: fields[0], op: "eq", value: "" }] })} className="h-7 text-xs bg-[color:var(--surface-2)]"><Plus className="w-3 h-3 mr-1" />Filter</Button>
    </div>
  );
}

function CondRules({ cfg, set, columns, showColumn = true }) {
  const cond = cfg.cond || [];
  const patch = (i, p) => set({ cond: cond.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  return (
    <div className="space-y-1.5 pt-1 border-t border-[color:var(--glass-border)]">
      <Label className="text-[11px] text-[color:var(--ink-3)]">Conditional formatting</Label>
      {cond.map((r, i) => (
        <div key={i} className="flex items-center gap-1 flex-wrap">
          {showColumn && (
            <Select value={r.column || ""} onValueChange={(v) => patch(i, { column: v })}>
              <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)] flex-1 min-w-[80px]"><SelectValue placeholder="measure" /></SelectTrigger>
              <SelectContent>{columns.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={r.op || "gt"} onValueChange={(v) => patch(i, { op: v })}>
            <SelectTrigger className="h-7 w-[58px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>{["gt", "gte", "lt", "lte", "eq"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" value={r.value ?? ""} onChange={(e) => patch(i, { value: e.target.value === "" ? null : Number(e.target.value) })} className="h-7 w-[64px] text-xs bg-[color:var(--surface-2)]" />
          <Select value={r.mode || "text"} onValueChange={(v) => patch(i, { mode: v })}>
            <SelectTrigger data-testid={`re-cond-mode-${i}`} className="h-7 w-[62px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="text">Text</SelectItem><SelectItem value="fill">Fill</SelectItem></SelectContent>
          </Select>
          <input type="color" value={r.color || "#E84A5F"} onChange={(e) => patch(i, { color: e.target.value })} className="w-7 h-7 rounded border border-[color:var(--glass-border)]" />
          <button onClick={() => set({ cond: cond.filter((_, j) => j !== i) })} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <Button data-testid="re-add-cond" variant="outline" size="sm" onClick={() => set({ cond: [...cond, { op: "gt", color: "#E84A5F", mode: "text" }] })} className="h-7 text-xs bg-[color:var(--surface-2)]"><Plus className="w-3 h-3 mr-1" />Rule</Button>
    </div>
  );
}

function CalcList({ cfg, set, availableCols }) {
  const calcs = cfg.calcs || [];
  const patch = (i, p) => set({ calcs: calcs.map((c, j) => (j === i ? { ...c, ...p } : c)) });
  return (
    <div className="space-y-1.5 pt-1 border-t border-[color:var(--glass-border)]">
      <Label className="text-[11px] text-[color:var(--ink-3)]">Calculated columns</Label>
      {calcs.map((c, i) => (
        <div key={i} className="flex items-center gap-1">
          <Input value={c.label || ""} onChange={(e) => patch(i, { label: e.target.value })} placeholder="Label" className="h-7 w-[80px] text-xs bg-[color:var(--surface-2)]" />
          <Input value={c.formula || ""} onChange={(e) => patch(i, { formula: e.target.value })} placeholder="[colA] / [colB] * 100" className="h-7 flex-1 text-xs bg-[color:var(--surface-2)]" />
          <button onClick={() => set({ calcs: calcs.filter((_, j) => j !== i) })} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <Button data-testid="re-add-calc" variant="outline" size="sm" onClick={() => set({ calcs: [...calcs, { label: "", formula: "" }] })} className="h-7 text-xs bg-[color:var(--surface-2)]"><Plus className="w-3 h-3 mr-1" />Calc</Button>
      {availableCols.length > 0 && <p className="text-[9px] text-[color:var(--ink-3)]">Reference columns as [name]. Available: {availableCols.map((c) => `[${c}]`).join(" ")} · operators + - * / ( )</p>}
    </div>
  );
}

export function ElementSettings({ el, patch, datasets }) {
  const cfg = el.config || {};
  const set = (p) => patch({ config: { ...cfg, ...p } });
  const allFields = fieldsOf(datasets, cfg.dataset_id).map((c) => c.name);
  const measureLabels = (cfg.measures || []).map((m) => (m.label || "").trim() || `${m.agg || "sum"}_${m.field}`);
  const calcLabels = (cfg.calcs || []).map((c) => (c.label || "").trim()).filter(Boolean);

  if (el.type === "heading" || el.type === "text") {
    return (
      <Sec title={el.type === "heading" ? "Heading" : "Text"}>
        <textarea data-testid="re-text" value={cfg.text || ""} onChange={(e) => set({ text: e.target.value })} rows={3}
          placeholder="Text… tokens: {date} {time} {report} {preparedBy} {param:Name}"
          className="w-full text-xs rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2" />
        {el.type === "heading" ? (
          <Row label="Level">
            <Select value={String(cfg.level || 1)} onValueChange={(v) => set({ level: Number(v) })}>
              <SelectTrigger className="h-7 w-[80px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3].map((l) => <SelectItem key={l} value={String(l)}>H{l}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
        ) : (
          <>
            <Row label="Size (px)"><Input type="number" value={cfg.size ?? 12} onChange={(e) => set({ size: Number(e.target.value) || 12 })} className="h-7 w-[70px] text-xs bg-[color:var(--surface-2)]" /></Row>
            <Row label="Align">
              <Select value={cfg.align || "left"} onValueChange={(v) => set({ align: v })}>
                <SelectTrigger className="h-7 w-[90px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                <SelectContent>{["left", "center", "right"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Bold"><Switch checked={!!cfg.bold} onCheckedChange={(v) => set({ bold: v })} /></Row>
            <Row label="Italic"><Switch checked={!!cfg.italic} onCheckedChange={(v) => set({ italic: v })} /></Row>
            <Row label="Color"><input type="color" value={cfg.color || "#1a2233"} onChange={(e) => set({ color: e.target.value })} className="w-8 h-7 rounded border border-[color:var(--glass-border)]" /></Row>
            <WidthRow cfg={cfg} set={set} />
          </>
        )}
      </Sec>
    );
  }
  if (el.type === "image") {
    return (
      <Sec title="Image">
        <Input data-testid="re-image-url" value={cfg.url || ""} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" className="h-8 text-xs bg-[color:var(--surface-2)]" />
        <Row label="Height (px)"><Input type="number" value={cfg.height ?? 120} onChange={(e) => set({ height: Number(e.target.value) || 120 })} className="h-7 w-[80px] text-xs bg-[color:var(--surface-2)]" /></Row>
        <Row label="Align">
          <Select value={cfg.align || "center"} onValueChange={(v) => set({ align: v })}>
            <SelectTrigger className="h-7 w-[90px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>{["left", "center", "right"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
      </Sec>
    );
  }
  if (el.type === "spacer") {
    return <Sec title="Spacer"><Row label="Height (px)"><Input type="number" value={cfg.height ?? 16} onChange={(e) => set({ height: Number(e.target.value) || 16 })} className="h-7 w-[80px] text-xs bg-[color:var(--surface-2)]" /></Row></Sec>;
  }
  if (el.type === "kpi") {
    return (
      <Sec title="KPI row">
        <DatasetPicker cfg={cfg} set={set} datasets={datasets} />
        {cfg.dataset_id && (
          <>
            <MeasureList cfg={cfg} set={set} datasets={datasets} />
            <ElementFilters cfg={cfg} set={set} datasets={datasets} />
          </>
        )}
        <WidthRow cfg={cfg} set={set} />
      </Sec>
    );
  }
  if (el.type === "chart") {
    return (
      <Sec title="Chart">
        <DatasetPicker cfg={cfg} set={set} datasets={datasets} />
        {cfg.dataset_id && (
          <>
            <Row label="Type">
              <Select value={cfg.chart_type || "column"} onValueChange={(v) => set({ chart_type: v })}>
                <SelectTrigger data-testid="re-chart-type" className="h-7 w-[110px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                <SelectContent>{["column", "bar", "line", "area", "pie", "donut"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <div>
              <Label className="text-[11px] text-[color:var(--ink-3)]">Category (X axis)</Label>
              <Select value={cfg.dimension?.field || ""} onValueChange={(v) => set({ dimension: { ...(cfg.dimension || {}), field: v } })}>
                <SelectTrigger data-testid="re-chart-dim" className="mt-1 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="field" /></SelectTrigger>
                <SelectContent>{allFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <MeasureList cfg={cfg} set={set} datasets={datasets} />
            <ElementFilters cfg={cfg} set={set} datasets={datasets} />
            <Row label="Height (px)"><Input type="number" value={cfg.height ?? 320} onChange={(e) => set({ height: Number(e.target.value) || 320 })} className="h-7 w-[80px] text-xs bg-[color:var(--surface-2)]" /></Row>
            <Row label="Limit"><Input type="number" value={cfg.limit ?? 20} onChange={(e) => set({ limit: Number(e.target.value) || 20 })} className="h-7 w-[80px] text-xs bg-[color:var(--surface-2)]" /></Row>
            <WidthRow cfg={cfg} set={set} />
          </>
        )}
      </Sec>
    );
  }
  if (el.type === "matrix") {
    return (
      <Sec title="Matrix (pivot)">
        <DatasetPicker cfg={cfg} set={set} datasets={datasets} />
        {cfg.dataset_id && (
          <>
            <div>
              <Label className="text-[11px] text-[color:var(--ink-3)]">Rows</Label>
              <Select value={cfg.row_field || ""} onValueChange={(v) => set({ row_field: v })}>
                <SelectTrigger data-testid="re-matrix-rows" className="mt-1 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="row field" /></SelectTrigger>
                <SelectContent>{allFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-[color:var(--ink-3)]">Columns</Label>
              <Select value={cfg.col_field || ""} onValueChange={(v) => set({ col_field: v })}>
                <SelectTrigger data-testid="re-matrix-cols" className="mt-1 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="column field" /></SelectTrigger>
                <SelectContent>{allFields.filter((f) => f !== cfg.row_field).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <MeasureList cfg={cfg} set={set} datasets={datasets} />
            <p className="text-[9px] text-[color:var(--ink-3)]">The first measure fills the matrix cells.</p>
            <Row label="Row totals"><Switch data-testid="re-matrix-rowtotals" checked={cfg.row_totals !== false} onCheckedChange={(v) => set({ row_totals: v })} /></Row>
            <Row label="Column totals"><Switch data-testid="re-matrix-coltotals" checked={cfg.col_totals !== false} onCheckedChange={(v) => set({ col_totals: v })} /></Row>
            <Row label="Row limit"><Input type="number" value={cfg.limit ?? 2000} onChange={(e) => set({ limit: Number(e.target.value) || 2000 })} className="h-7 w-[80px] text-xs bg-[color:var(--surface-2)]" /></Row>
            <ElementFilters cfg={cfg} set={set} datasets={datasets} />
            <CondRules cfg={cfg} set={set} columns={[]} showColumn={false} />
            <WidthRow cfg={cfg} set={set} />
          </>
        )}
      </Sec>
    );
  }
  if (el.type === "table") {
    const cols = cfg.columns || [];
    return (
      <Sec title="Table">
        <DatasetPicker cfg={cfg} set={set} datasets={datasets} />
        {cfg.dataset_id && (
          <>
            <div className="space-y-1.5">
              <Label className="text-[11px] text-[color:var(--ink-3)]">Columns (dimensions)</Label>
              {cols.map((c, i) => (
                <div key={i} className="flex items-center gap-1">
                  <Select value={c.field || ""} onValueChange={(v) => set({ columns: cols.map((x, j) => (j === i ? { ...x, field: v } : x)) })}>
                    <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)] flex-1"><SelectValue placeholder="field" /></SelectTrigger>
                    <SelectContent>{allFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                  <button onClick={() => set({ columns: cols.filter((_, j) => j !== i) })} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <Button data-testid="re-add-column" variant="outline" size="sm" onClick={() => set({ columns: [...cols, { field: allFields[0] }] })} className="h-7 text-xs bg-[color:var(--surface-2)]"><Plus className="w-3 h-3 mr-1" />Column</Button>
            </div>
            <MeasureList cfg={cfg} set={set} datasets={datasets} />
            <div>
              <Label className="text-[11px] text-[color:var(--ink-3)]">Group by (with subtotals)</Label>
              <Select value={cfg.group_by || "__none__"} onValueChange={(v) => set({ group_by: v === "__none__" ? null : v, ...(v === "__none__" ? { group_by2: null } : {}) })}>
                <SelectTrigger data-testid="re-groupby" className="mt-1 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No grouping</SelectItem>
                  {allFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {cfg.group_by && (
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Then group by (nested)</Label>
                <Select value={cfg.group_by2 || "__none__"} onValueChange={(v) => set({ group_by2: v === "__none__" ? null : v })}>
                  <SelectTrigger data-testid="re-groupby2" className="mt-1 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No nested grouping</SelectItem>
                    {allFields.filter((f) => f !== cfg.group_by).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Sort by</Label>
                <Select value={cfg.sort?.field || "__none__"} onValueChange={(v) => set({ sort: v === "__none__" ? null : { ...(cfg.sort || {}), field: v } })}>
                  <SelectTrigger className="mt-1 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="none" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {[...cols.map((c) => c.field), ...measureLabels, ...calcLabels].filter(Boolean).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Direction</Label>
                <Select value={cfg.sort?.dir || "asc"} onValueChange={(v) => set({ sort: { ...(cfg.sort || {}), dir: v } })}>
                  <SelectTrigger className="mt-1 h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="asc">Ascending</SelectItem><SelectItem value="desc">Descending</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <Row label="Subtotals"><Switch checked={cfg.subtotals !== false} onCheckedChange={(v) => set({ subtotals: v })} /></Row>
            <Row label="Grand total"><Switch checked={cfg.totals !== false} onCheckedChange={(v) => set({ totals: v })} /></Row>
            <Row label="Zebra rows"><Switch checked={cfg.zebra !== false} onCheckedChange={(v) => set({ zebra: v })} /></Row>
            <Row label="Row limit"><Input type="number" value={cfg.limit ?? 500} onChange={(e) => set({ limit: Number(e.target.value) || 500 })} className="h-7 w-[80px] text-xs bg-[color:var(--surface-2)]" /></Row>
            <WidthRow cfg={cfg} set={set} />
            <ElementFilters cfg={cfg} set={set} datasets={datasets} />
            <CalcList cfg={cfg} set={set} availableCols={measureLabels} />
            <CondRules cfg={cfg} set={set} columns={[...measureLabels, ...calcLabels]} />
          </>
        )}
      </Sec>
    );
  }
  return null;
}

export function ParametersEditor({ parameters = [], onChange, datasets }) {
  const patch = (i, p) => onChange(parameters.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <Sec title="Parameters (prompted at run time)">
      {parameters.map((p, i) => (
        <div key={i} className="rounded-lg border border-[color:var(--glass-border)] p-2 space-y-1.5">
          <div className="flex items-center gap-1">
            <Input value={p.name || ""} onChange={(e) => patch(i, { name: e.target.value })} placeholder="Name (e.g. Region)" className="h-7 text-xs bg-[color:var(--surface-2)]" />
            <button onClick={() => onChange(parameters.filter((_, j) => j !== i))} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Select value={p.dataset_id || ""} onValueChange={(v) => patch(i, { dataset_id: v, field: null })}>
              <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="dataset" /></SelectTrigger>
              <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={p.field || ""} onValueChange={(v) => patch(i, { field: v })}>
              <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="field" /></SelectTrigger>
              <SelectContent>{fieldsOf(datasets, p.dataset_id).map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Select value={p.op || "eq"} onValueChange={(v) => patch(i, { op: v })}>
              <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
              <SelectContent>{FILTER_OPS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={p.default ?? ""} onChange={(e) => patch(i, { default: e.target.value })} placeholder="default value" className="h-7 text-xs bg-[color:var(--surface-2)]" />
          </div>
        </div>
      ))}
      <Button data-testid="re-add-param" variant="outline" size="sm" onClick={() => onChange([...parameters, { id: `p_${Date.now()}`, name: "", op: "eq" }])} className="h-7 text-xs bg-[color:var(--surface-2)]"><Plus className="w-3 h-3 mr-1" />Parameter</Button>
      <p className="text-[9px] text-[color:var(--ink-3)]">Use parameter values in text via {"{param:Name}"} tokens.</p>
    </Sec>
  );
}

export function ReportFiltersEditor({ filters = [], onChange, datasets }) {
  const patch = (i, p) => onChange(filters.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <Sec title="Report filters (all elements)">
      <p className="text-[9px] text-[color:var(--ink-3)]">Applied to every element bound to the chosen dataset.</p>
      {filters.map((f, i) => (
        <div key={i} className="rounded-lg border border-[color:var(--glass-border)] p-2 space-y-1.5">
          <div className="flex items-center gap-1">
            <Select value={f.dataset_id || ""} onValueChange={(v) => patch(i, { dataset_id: v, field: null })}>
              <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)] flex-1"><SelectValue placeholder="dataset" /></SelectTrigger>
              <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={() => onChange(filters.filter((_, j) => j !== i))} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Select value={f.field || ""} onValueChange={(v) => patch(i, { field: v })}>
              <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="field" /></SelectTrigger>
              <SelectContent>{fieldsOf(datasets, f.dataset_id).map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.op || "eq"} onValueChange={(v) => patch(i, { op: v })}>
              <SelectTrigger className="h-7 text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
              <SelectContent>{FILTER_OPS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input value={f.value ?? ""} onChange={(e) => patch(i, { value: e.target.value })} placeholder="value (comma list for 'in list')" className="h-7 text-xs bg-[color:var(--surface-2)]" />
        </div>
      ))}
      <Button data-testid="re-add-report-filter" variant="outline" size="sm" onClick={() => onChange([...filters, { dataset_id: datasets[0]?.id || "", op: "eq", value: "" }])} className="h-7 text-xs bg-[color:var(--surface-2)]"><Plus className="w-3 h-3 mr-1" />Report filter</Button>
    </Sec>
  );
}

export function PageSettings({ report, patch }) {
  const pg = report.page || {};
  const hdr = report.header || {};
  const ftr = report.footer || {};
  const theme = report.theme || {};
  return (
    <>
      <Sec title="Page setup">
        <Row label="Size">
          <Select value={pg.size || "A4"} onValueChange={(v) => patch({ page: { ...pg, size: v } })}>
            <SelectTrigger data-testid="re-page-size" className="h-7 w-[90px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>{["A4", "A3", "A5", "Letter", "Legal"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        <Row label="Orientation">
          <Select value={pg.orientation || "portrait"} onValueChange={(v) => patch({ page: { ...pg, orientation: v } })}>
            <SelectTrigger data-testid="re-orientation" className="h-7 w-[110px] text-xs bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="portrait">Portrait</SelectItem><SelectItem value="landscape">Landscape</SelectItem></SelectContent>
          </Select>
        </Row>
        <Row label="Margin (mm)"><Input type="number" value={pg.margin ?? 15} onChange={(e) => patch({ page: { ...pg, margin: Number(e.target.value) || 15 } })} className="h-7 w-[70px] text-xs bg-[color:var(--surface-2)]" /></Row>
      </Sec>
      <Sec title="Header & footer">
        <Row label="Show header"><Switch checked={hdr.show !== false} onCheckedChange={(v) => patch({ header: { ...hdr, show: v } })} /></Row>
        <Input value={hdr.left || ""} onChange={(e) => patch({ header: { ...hdr, left: e.target.value } })} placeholder="Header left — {report}" className="h-7 text-xs bg-[color:var(--surface-2)]" />
        <Input value={hdr.right || ""} onChange={(e) => patch({ header: { ...hdr, right: e.target.value } })} placeholder="Header right — {date}" className="h-7 text-xs bg-[color:var(--surface-2)]" />
        <Row label="Show footer"><Switch checked={ftr.show !== false} onCheckedChange={(v) => patch({ footer: { ...ftr, show: v } })} /></Row>
        <Input value={ftr.center || ""} onChange={(e) => patch({ footer: { ...ftr, center: e.target.value } })} placeholder="Footer — Page {page} of {pages}" className="h-7 text-xs bg-[color:var(--surface-2)]" />
        <p className="text-[9px] text-[color:var(--ink-3)]">Tokens: {"{report} {date} {time} {page} {pages} {preparedBy} {param:Name}"}</p>
      </Sec>
      <Sec title="Theme & watermark">
        <Row label="Use workspace brand">
          <Switch data-testid="re-theme-use-brand" checked={theme.use_brand !== false} onCheckedChange={(v) => patch({ theme: { ...theme, use_brand: v } })} />
        </Row>
        <p className="text-[9px] text-[color:var(--ink-3)] -mt-1">When on, the workspace logo &amp; brand color are applied automatically. Overrides below still win.</p>
        <Row label="Primary color"><input type="color" value={theme.primary || "#2B4EFF"} onChange={(e) => patch({ theme: { ...theme, primary: e.target.value } })} className="w-8 h-7 rounded border border-[color:var(--glass-border)]" data-testid="re-theme-primary" /></Row>
        <Input data-testid="re-theme-logo" value={theme.logo_url || ""} onChange={(e) => patch({ theme: { ...theme, logo_url: e.target.value } })} placeholder="Report logo URL (optional override)" className="h-7 text-xs bg-[color:var(--surface-2)]" />
        <Input value={(report.watermark || {}).text || ""} onChange={(e) => patch({ watermark: { ...(report.watermark || {}), text: e.target.value } })} placeholder="Watermark text (e.g. CONFIDENTIAL)" className="h-7 text-xs bg-[color:var(--surface-2)]" data-testid="re-watermark" />
      </Sec>
    </>
  );
}
